import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator, faClock, faCoins, faUsers,
  faArrowRight, faBolt, faArrowTrendUp, faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/LanguageContext';
import './ROICalculator.css';

// Devises supportées. Les bornes du slider "coût horaire" et le coût de référence
// d'un projet d'automatisation sont adaptés à la monnaie locale.
// Taux approximatifs (vs EUR) — ajustables sans refaire le calcul, on stocke
// tout dans la devise choisie directement.
const CURRENCIES = {
  EUR: {
    code: 'EUR',
    symbol: '€',
    locale: 'fr-FR',
    hourly: { default: 35, min: 15, max: 120, step: 5 },
    projectCost: 8000
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    locale: 'pl-PL',
    hourly: { default: 150, min: 60, max: 500, step: 10 },
    projectCost: 34000
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    locale: 'de-CH',
    hourly: { default: 35, min: 15, max: 120, step: 5 },
    projectCost: 7600
  },
  USD: {
    code: 'USD',
    symbol: '$',
    locale: 'en-US',
    hourly: { default: 40, min: 15, max: 130, step: 5 },
    projectCost: 8600
  }
};

const pickDefaultCurrency = (language) => {
  if (language === 'pl') return 'PLN';
  if (language === 'en') return 'USD';
  return 'EUR';
};

const ROICalculator = () => {
  const { t, language } = useLanguage();

  const [currency, setCurrency] = useState(() => pickDefaultCurrency(language));
  const cfg = CURRENCIES[currency];

  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [employees, setEmployees] = useState(3);
  const [hourlyCost, setHourlyCost] = useState(cfg.hourly.default);
  const [automationRate, setAutomationRate] = useState(70);

  // Si l'utilisateur change de devise, on ramène le slider "coût horaire" sur la
  // valeur par défaut de la nouvelle devise pour éviter des nombres incohérents
  // (ex: 35 PLN au lieu de 150 PLN, ou 150 EUR = surréaliste).
  useEffect(() => {
    setHourlyCost(CURRENCIES[currency].hourly.default);
  }, [currency]);

  const results = useMemo(() => {
    const weeklyHoursSaved = hoursPerWeek * employees * (automationRate / 100);
    const weeklySaved = weeklyHoursSaved * hourlyCost;
    const monthlySavings = weeklySaved * 4.33;
    const yearlySavings = weeklySaved * 52;
    const projectCost = CURRENCIES[currency].projectCost;
    const paybackMonths = monthlySavings > 0
      ? Math.max(1, Math.round((projectCost / monthlySavings) * 10) / 10)
      : 0;

    return {
      weeklyHours: Math.round(weeklyHoursSaved),
      monthly: Math.round(monthlySavings),
      yearly: Math.round(yearlySavings),
      payback: paybackMonths
    };
  }, [hoursPerWeek, employees, hourlyCost, automationRate, currency]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: cfg.code,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section className="section section-darker roi-section">
      <div className="grid-pattern"></div>
      <div className="roi-section__glow"></div>
      <div className="container">
        <div className="fade-in" style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="section-label">
            <FontAwesomeIcon icon={faCalculator} style={{ marginRight: 6 }} />
            {t('roi.label')}
          </span>
          <h2 className="section-title">
            {t('roi.titleStart')} <span className="gradient-text">{t('roi.titleHighlight')}</span> {t('roi.titleEnd')}
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto' }}>
            {t('roi.subtitle')}
          </p>
        </div>

        <div className="roi-calculator fade-in">
          {/* INPUTS */}
          <div className="roi-inputs">
            <div className="roi-inputs__header">
              <h3 className="roi-inputs__title">{t('roi.inputTitle')}</h3>
              <div className="roi-currency" role="group" aria-label={t('roi.currencyLabel')}>
                <span className="roi-currency__label">{t('roi.currencyLabel')}</span>
                <div className="roi-currency__options">
                  {Object.keys(CURRENCIES).map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={`roi-currency__btn${currency === code ? ' roi-currency__btn--active' : ''}`}
                      onClick={() => setCurrency(code)}
                      aria-pressed={currency === code}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="roi-field">
              <label className="roi-field__label">
                <FontAwesomeIcon icon={faClock} className="roi-field__icon" />
                <span>{t('roi.hoursLabel')}</span>
                <span className="roi-field__value">{hoursPerWeek}h</span>
              </label>
              <input
                type="range"
                min="1"
                max="40"
                value={hoursPerWeek}
                onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                className="roi-field__slider"
              />
              <span className="roi-field__help">{t('roi.hoursHelp')}</span>
            </div>

            <div className="roi-field">
              <label className="roi-field__label">
                <FontAwesomeIcon icon={faUsers} className="roi-field__icon" />
                <span>{t('roi.employeesLabel')}</span>
                <span className="roi-field__value">{employees}</span>
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={employees}
                onChange={(e) => setEmployees(Number(e.target.value))}
                className="roi-field__slider"
              />
            </div>

            <div className="roi-field">
              <label className="roi-field__label">
                <FontAwesomeIcon icon={faCoins} className="roi-field__icon" />
                <span>{t('roi.hourlyCostLabel')}</span>
                <span className="roi-field__value">{hourlyCost} {cfg.symbol}</span>
              </label>
              <input
                type="range"
                min={cfg.hourly.min}
                max={cfg.hourly.max}
                step={cfg.hourly.step}
                value={hourlyCost}
                onChange={(e) => setHourlyCost(Number(e.target.value))}
                className="roi-field__slider"
              />
              <span className="roi-field__help">{t('roi.hourlyCostHelp')}</span>
            </div>

            <div className="roi-field">
              <label className="roi-field__label">
                <FontAwesomeIcon icon={faBolt} className="roi-field__icon" />
                <span>{t('roi.automationLabel')}</span>
                <span className="roi-field__value">{automationRate} %</span>
              </label>
              <input
                type="range"
                min="20"
                max="95"
                step="5"
                value={automationRate}
                onChange={(e) => setAutomationRate(Number(e.target.value))}
                className="roi-field__slider"
              />
              <span className="roi-field__help">{t('roi.automationHelp')}</span>
            </div>
          </div>

          {/* RESULTS */}
          <div className="roi-results">
            <h3 className="roi-results__title">{t('roi.resultsTitle')}</h3>

            <div className="roi-result roi-result--hours">
              <div className="roi-result__icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="roi-result__content">
                <span className="roi-result__value">{results.weeklyHours}h</span>
                <span className="roi-result__label">{t('roi.weeklyHours')}</span>
              </div>
            </div>

            <div className="roi-result">
              <div className="roi-result__icon roi-result__icon--money">
                <FontAwesomeIcon icon={faArrowTrendUp} />
              </div>
              <div className="roi-result__content">
                <span className="roi-result__value roi-result__value--big">{formatMoney(results.monthly)}</span>
                <span className="roi-result__label">{t('roi.monthlySavings')}</span>
              </div>
            </div>

            <div className="roi-result roi-result--highlight">
              <div className="roi-result__icon roi-result__icon--big">
                <FontAwesomeIcon icon={faCoins} />
              </div>
              <div className="roi-result__content">
                <span className="roi-result__value roi-result__value--xl">{formatMoney(results.yearly)}</span>
                <span className="roi-result__label">{t('roi.yearlySavings')}</span>
              </div>
            </div>

            <div className="roi-payback">
              <div className="roi-payback__left">
                <FontAwesomeIcon icon={faHourglassHalf} />
                <div>
                  <strong>{t('roi.paybackTitle')}</strong>
                  <span>{t('roi.paybackDesc')}</span>
                </div>
              </div>
              <div className="roi-payback__right">
                <span className="roi-payback__value">{results.payback}</span>
                <span className="roi-payback__label">{t('roi.paybackValue')}</span>
              </div>
            </div>

            <Link to="/contact" className="btn btn-primary roi-results__cta">
              {t('roi.cta')} <FontAwesomeIcon icon={faArrowRight} />
            </Link>

            <p className="roi-disclaimer">{t('roi.disclaimer')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
